import { ElementRef, Injectable } from "@angular/core";

import {
    BehaviorSubject,
    distinctUntilChanged,
    filter,
    map,
    merge,
    Observable,
    share,
    Subject,
    tap,
} from "rxjs";

import { PinchEvent } from "@hermes/utils-generic/directives/gestures";

import { Coordinate, ThumbMove, WithParent } from "../types/zoom.types";

@Injectable()
export class ZoomService {
    public static minScale = 1;
    public static maxScale = 3;

    public scale$: Observable<number>;
    public scaleIncrement$ = new Subject<number>();
    public scaleDecrement$ = new Subject<number>();
    public pinchScale$ = new Subject<number>();
    public thumbMoved$: Observable<ThumbMove>;
    public track: Pick<DOMRect, "bottom" | "top" | "height"> = {
        bottom: 0,
        height: 0,
        top: 0,
    };

    private readonly resetScaleSubject = new Subject<number>();
    private readonly scaleSubjectFromThumb = new Subject<number>();
    private readonly loadedAssetSubject = new BehaviorSubject<
        HTMLImageElement | undefined
    >(undefined);

    private currentScale = ZoomService.minScale;

    constructor() {
        const loadedAssetScale$ = this.loadedAssetSubject.pipe(
            filter((image) => image !== undefined),
            map((image) => this.computeScale(image)),
        );

        this.scale$ = merge(
            this.resetScaleSubject,
            this.scaleIncrement$,
            this.scaleDecrement$,
            this.pinchScale$,
            loadedAssetScale$,
            this.scaleSubjectFromThumb,
        ).pipe(
            map((freeScale) => this.clampScale(freeScale)),
            distinctUntilChanged(),
            tap((scale) => {
                this.currentScale = scale;
            }),
            share(),
        );

        this.thumbMoved$ = merge(
            this.scaleIncrement$,
            this.scaleDecrement$,
            this.pinchScale$,
            loadedAssetScale$,
            this.scaleSubjectFromThumb,
        ).pipe(map((scale) => this.getPositionFrom(this.clampScale(scale))));
    }

    public resetScale() {
        this.resetScaleSubject.next(ZoomService.minScale);
    }

    public scaleImageFromThumb(position: number): void {
        this.scaleSubjectFromThumb.next(this.getScaleFrom(position));
    }

    public setTrackRect({ nativeElement }: ElementRef<HTMLElement>) {
        const { bottom, height, top } = nativeElement.getBoundingClientRect();
        this.track = {
            bottom,
            height,
            top,
        };
    }

    public pinch(event: PinchEvent): void {
        this.pinchScale$.next(event.scale);
    }

    public updateScale(scale: number): void {
        this.pinchScale$.next(scale);
    }

    public scaleForHighQuality(image: HTMLImageElement): void {
        this.loadedAssetSubject.next(image);
    }

    public getPositionFrom(scale: number): ThumbMove {
        const ratio = (scale - ZoomService.minScale) / this.scaleRange();
        const position = ratio * this.track.height;
        const scalePercentage = this.format(scale * 100, 0);
        return { scalePercentage, position };
    }

    public increment(step: number): void {
        if (this.canIncrement()) {
            const precision = this.currentScale > ZoomService.minScale ? 1 : 2;
            const scale = this.currentScale + step;
            this.scaleIncrement$.next(this.format(scale, precision));
        }
    }

    public decrement(step: number): void {
        if (this.canDecrement()) {
            const precision = this.currentScale < ZoomService.maxScale ? 1 : 2;
            const scale = this.currentScale - step;
            this.scaleDecrement$.next(this.format(scale, precision));
        }
    }

    public isMinScale(scale: number): boolean {
        return scale === ZoomService.minScale;
    }

    public restrictPosition(
        { x, y }: Coordinate,
        boundary: HTMLElement,
    ): Coordinate {
        const maxPanOffset = 0.3;
        const offsetX =
            (boundary.children[0] as HTMLElement).getBoundingClientRect()
                .width * maxPanOffset;
        const offsetY =
            (boundary.children[0] as HTMLElement).getBoundingClientRect()
                .height * maxPanOffset;
        return {
            x: this.clampAxis(x, offsetX),
            y: this.clampAxis(y, offsetY),
        };
    }

    /*
     * Zoom utility functions
     */

    private getScaleFrom(position: number) {
        const ratio = position / this.track.height;
        const scale = ZoomService.minScale + this.scaleRange() * ratio;
        return this.format(scale, 2);
    }

    private clampAxis(x: Coordinate["x"], max: Coordinate["x"]): number {
        return this.clamp(x, -max, max);
    }

    /* due to floating numbers inaccuracy, scaled image does not fill
     ** screen at 100% everytimes, so we use an error margin
     */
    private computeScale(image: HTMLImageElement | undefined): number {
        if (!image || !this.hasParent(image)) {
            return 1;
        }
        const marginError = 0;
        const parentWdith = image.parentElement.getBoundingClientRect().width;
        const imageWdith = image.getBoundingClientRect().width;
        const scale = parentWdith / imageWdith;

        return this.format(scale + marginError, 2);
    }

    private canIncrement(): boolean {
        return this.currentScale < ZoomService.maxScale;
    }

    private canDecrement(): boolean {
        return this.currentScale > ZoomService.minScale;
    }

    private clampScale(scale: number) {
        return this.clamp(scale, ZoomService.minScale, ZoomService.maxScale);
    }

    private clamp(value: number, min: number, max: number) {
        return Math.min(Math.max(value, min), max);
    }

    private scaleRange(): number {
        return ZoomService.maxScale - ZoomService.minScale;
    }

    private format(value: number, precision: number) {
        return Number(value.toFixed(precision));
    }

    private hasParent(image: HTMLElement): image is WithParent<HTMLElement> {
        return "parentElement" in image && image.parentElement !== null;
    }
}
